import { Chip } from '@mui/material';
import React from 'react';

import StackedIcon from '@components/Icon/StackedIcon';

import { getObservationConfig } from '@constants/fhir';

import useReferenceRange from '@hooks/useReferenceRange';

import type {
  AlertRiskParameterModel,
  ParameterModel,
  ReferenceRangeRiskLevel,
} from 'generated/types';

interface AlertParameterChipProps {
  code: string;
  range: AlertRiskParameterModel[];
  risk: ReferenceRangeRiskLevel;
  type?: string | null;
}

/**
 * Alert parameter chip used in alert table
 */
export default function AlertParameterChip({
  code,
  range,
  risk,
  type,
}: AlertParameterChipProps) {
  const signType = getObservationConfig(code);
  const { displayFriendlyRange, formatAlertParameterLabel, parameterMap } =
    useReferenceRange();

  if (!signType) {
    return null;
  }
  const { icon } = signType;
  const backgroundColour = parameterMap[risk].bgColour;
  const color = parameterMap[risk].colour;

  const title = formatAlertParameterLabel({
    code,
    friendly_label: displayFriendlyRange({
      code,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      parameters: range.map(({ risk, ...rest }) => ({
        ...(rest as ParameterModel),
      })),
      include_clusivity: false,
      shorthand: true,
    }),
    risk,
    range_code: type,
    is_custom: range.some(({ vitall_patient_id }) => vitall_patient_id),
  });

  return (
    <Chip
      icon={
        <span className="fa-1x">
          <StackedIcon
            icons={icon.map((i) => ({
              key: i.name,
              type: 'fas',
              ...i,
              color,
            }))}
          />
        </span>
      }
      label={title}
      sx={{
        bgcolor: backgroundColour,
        borderColor: color,
        textTransform: 'none',
        px: 0.5,
        py: 0.5,
        height: 'auto',
        width: 'fit-content',
        '& .MuiChip-label': {
          display: 'block',
          whiteSpace: 'normal',
          lineHeight: 'normal',
        },
      }}
      variant="outlined"
    />
  );
}
